import {
    ArrowRight,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    CircleAlert,
    CirclePlus,
    Copy,
    LoaderCircle,
    TrendingDown,
    TrendingUp,
    Trash2,
    WandSparkles,
    X,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './PayrollSimulatorScreen.module.css'

const CHANGE_TYPES = [
    { key: 'COMMISSION_RATE_CHANGE', label: 'Commission Rate', icon: '📊' },
    { key: 'BASIC_INCREMENT', label: 'Basic Increment', icon: '📈' },
    { key: 'ADD_ALLOWANCE', label: 'Add Allowance', icon: '🎁' },
    { key: 'REMOVE_COMPONENT', label: 'Remove Component', icon: '❌' },
]

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount, decimals = 0) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })
}

const PayrollSimulatorScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [changes, setChanges] = useState([])
    const [simResult, setSimResult] = useState(null)
    const [isSimulating, setIsSimulating] = useState(false)
    const [selectedChangeType, setSelectedChangeType] = useState(null)
    const [changeForm, setChangeForm] = useState({})
    const [resultsExpanded, setResultsExpanded] = useState(true)

    // Change builder state
    const [commissionType, setCommissionType] = useState('SERVICE_COMMISSION')
    const [commissionRate, setCommissionRate] = useState('')
    const [commissionTemplate, setCommissionTemplate] = useState('')

    const [incrementType, setIncrementType] = useState('FLAT') // FLAT | PERCENT
    const [incrementAmount, setIncrementAmount] = useState('')
    const [incrementPercent, setIncrementPercent] = useState('')
    const [incrementStaffScope, setIncrementStaffScope] = useState('ALL')
    const [incrementStaffIds, setIncrementStaffIds] = useState([])

    const [allowanceAmount, setAllowanceAmount] = useState('')
    const [allowanceLabel, setAllowanceLabel] = useState('')
    const [allowanceScope, setAllowanceScope] = useState('ALL')
    const [allowanceStaffIds, setAllowanceStaffIds] = useState([])

    const [removeComponentCode, setRemoveComponentCode] = useState('')

    const runSimulation = useCallback(async () => {
        if (changes.length === 0) {
            toast.error('Add at least one change to simulate.')
            return
        }

        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Store or auth context is missing.')
            return
        }

        try {
            setIsSimulating(true)

            const response = await fetch(BASE_URL_API + 'simulatePayrollChanges', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    hypotheticalChanges: changes,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setSimResult(respo.response)
                setResultsExpanded(true)
                toast.success('Simulation complete.')
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'PAY_008':
                    toast.error('No finalized payroll run found. Run and finalize a payroll cycle first.')
                    break
                case 'VAL_001':
                case 'VAL_002':
                case 'VAL_003':
                    toast.error(respo?.message || 'Invalid changes.')
                    break
                default:
                    toast.error(respo?.message || 'Simulation failed.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsSimulating(false)
        }
    }, [changes, authCtx, activeStore])

    const addCommissionChange = useCallback(() => {
        if (!commissionRate || Number(commissionRate) < 0) {
            toast.error('Enter a valid commission rate (0 or greater).')
            return
        }

        const change = {
            changeType: 'COMMISSION_RATE_CHANGE',
            componentType: commissionType,
            newRate: Number(commissionRate),
        }
        if (commissionTemplate) {
            change.templateIdentifier = commissionTemplate
        }

        setChanges((prev) => [...prev, change])
        setSelectedChangeType(null)
        setCommissionRate('')
        setCommissionTemplate('')
        toast.success('Commission rate change added.')
    }, [commissionRate, commissionTemplate, commissionType])

    const addIncrementChange = useCallback(() => {
        const isFlat = incrementType === 'FLAT'
        const value = isFlat ? incrementAmount : incrementPercent

        if (!value || Number(value) < 0) {
            toast.error(`Enter a valid ${isFlat ? 'amount' : 'percentage'} (0 or greater).`)
            return
        }

        if (incrementStaffScope === 'SPECIFIC' && incrementStaffIds.length === 0) {
            toast.error('Select at least one staff member.')
            return
        }

        const change = {
            changeType: 'BASIC_INCREMENT',
            ...(isFlat ? { incrementAmount: Number(value) } : { incrementPercent: Number(value) }),
        }

        if (incrementStaffScope === 'SPECIFIC') {
            change.staffIdentifiers = incrementStaffIds
        }

        setChanges((prev) => [...prev, change])
        setSelectedChangeType(null)
        setIncrementType('FLAT')
        setIncrementAmount('')
        setIncrementPercent('')
        setIncrementStaffScope('ALL')
        setIncrementStaffIds([])
        toast.success('Basic increment change added.')
    }, [incrementType, incrementAmount, incrementPercent, incrementStaffScope, incrementStaffIds])

    const addAllowanceChange = useCallback(() => {
        if (!allowanceAmount || Number(allowanceAmount) < 0) {
            toast.error('Enter a valid allowance amount (0 or greater).')
            return
        }

        if (!allowanceLabel.trim()) {
            toast.error('Provide an allowance label.')
            return
        }

        if (allowanceScope === 'SPECIFIC' && allowanceStaffIds.length === 0) {
            toast.error('Select at least one staff member.')
            return
        }

        const change = {
            changeType: 'ADD_ALLOWANCE',
            amountPerStaff: Number(allowanceAmount),
            allowanceLabel: allowanceLabel.trim(),
            appliesToAll: allowanceScope === 'ALL',
        }

        if (allowanceScope === 'SPECIFIC') {
            change.staffIdentifiers = allowanceStaffIds
        }

        setChanges((prev) => [...prev, change])
        setSelectedChangeType(null)
        setAllowanceAmount('')
        setAllowanceLabel('')
        setAllowanceScope('ALL')
        setAllowanceStaffIds([])
        toast.success('Allowance change added.')
    }, [allowanceAmount, allowanceLabel, allowanceScope, allowanceStaffIds])

    const addRemoveComponentChange = useCallback(() => {
        if (!removeComponentCode.trim()) {
            toast.error('Enter a component code.')
            return
        }

        const change = {
            changeType: 'REMOVE_COMPONENT',
            componentCode: removeComponentCode.trim().toUpperCase(),
        }

        setChanges((prev) => [...prev, change])
        setSelectedChangeType(null)
        setRemoveComponentCode('')
        toast.success('Remove component change added.')
    }, [removeComponentCode])

    const removeChange = useCallback((index) => {
        setChanges((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const clearAllChanges = useCallback(() => {
        setChanges([])
        setSimResult(null)
    }, [])

    const perStaffImpact = useMemo(() => {
        if (!simResult?.perStaffImpact) return []
        const sorted = [...simResult.perStaffImpact].sort((a, b) => b.delta - a.delta)
        return sorted
    }, [simResult])

    const handleChangeTypeSelect = (typeKey) => {
        setSelectedChangeType(typeKey)
    }

    const renderChangeForm = () => {
        if (!selectedChangeType) return null

        switch (selectedChangeType) {
            case 'COMMISSION_RATE_CHANGE':
                return (
                    <div className={styles.changeForm}>
                        <div className={styles.formField}>
                            <label>Component Type</label>
                            <input
                                type="text"
                                value={commissionType}
                                onChange={(e) => setCommissionType(e.target.value)}
                                placeholder="e.g., SERVICE_COMMISSION"
                            />
                        </div>
                        <div className={styles.formField}>
                            <label>New Rate (%)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={commissionRate}
                                onChange={(e) => setCommissionRate(e.target.value)}
                                placeholder="e.g., 15.00"
                            />
                        </div>
                        <div className={styles.formField}>
                            <label>Template (Optional)</label>
                            <input
                                type="text"
                                value={commissionTemplate}
                                onChange={(e) => setCommissionTemplate(e.target.value)}
                                placeholder="Leave blank for all templates"
                            />
                        </div>
                        <div className={styles.formActions}>
                            <button className={styles.secondaryBtn} onClick={() => setSelectedChangeType(null)}>
                                Cancel
                            </button>
                            <button className={styles.primaryBtn} onClick={addCommissionChange}>
                                Add Change
                            </button>
                        </div>
                    </div>
                )

            case 'BASIC_INCREMENT':
                return (
                    <div className={styles.changeForm}>
                        <div className={styles.formToggle}>
                            <button
                                className={incrementType === 'FLAT' ? styles.toggleActive : ''}
                                onClick={() => setIncrementType('FLAT')}
                            >
                                Flat ₹
                            </button>
                            <button
                                className={incrementType === 'PERCENT' ? styles.toggleActive : ''}
                                onClick={() => setIncrementType('PERCENT')}
                            >
                                Percentage %
                            </button>
                        </div>

                        <div className={styles.formField}>
                            <label>{incrementType === 'FLAT' ? 'Amount (₹)' : 'Percentage (%)'}</label>
                            <input
                                type="number"
                                min="0"
                                step={incrementType === 'FLAT' ? '100' : '0.1'}
                                value={incrementType === 'FLAT' ? incrementAmount : incrementPercent}
                                onChange={(e) => {
                                    if (incrementType === 'FLAT') {
                                        setIncrementAmount(e.target.value)
                                    } else {
                                        setIncrementPercent(e.target.value)
                                    }
                                }}
                                placeholder={`e.g., ${incrementType === 'FLAT' ? '1000' : '10'}`}
                            />
                        </div>

                        <div className={styles.formField}>
                            <label>Apply To</label>
                            <div className={styles.radioGroup}>
                                <label>
                                    <input
                                        type="radio"
                                        value="ALL"
                                        checked={incrementStaffScope === 'ALL'}
                                        onChange={() => setIncrementStaffScope('ALL')}
                                    />
                                    All staff
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        value="SPECIFIC"
                                        checked={incrementStaffScope === 'SPECIFIC'}
                                        onChange={() => setIncrementStaffScope('SPECIFIC')}
                                    />
                                    Specific staff (IDs comma-separated)
                                </label>
                            </div>
                            {incrementStaffScope === 'SPECIFIC' ? (
                                <input
                                    type="text"
                                    value={incrementStaffIds.join(', ')}
                                    onChange={(e) => {
                                        const ids = e.target.value
                                            .split(',')
                                            .map((id) => id.trim())
                                            .filter(Boolean)
                                        setIncrementStaffIds(ids)
                                    }}
                                    placeholder="e.g., ID1, ID2, ID3"
                                />
                            ) : null}
                        </div>

                        <div className={styles.formActions}>
                            <button className={styles.secondaryBtn} onClick={() => setSelectedChangeType(null)}>
                                Cancel
                            </button>
                            <button className={styles.primaryBtn} onClick={addIncrementChange}>
                                Add Change
                            </button>
                        </div>
                    </div>
                )

            case 'ADD_ALLOWANCE':
                return (
                    <div className={styles.changeForm}>
                        <div className={styles.formField}>
                            <label>Amount per Staff (₹)</label>
                            <input
                                type="number"
                                min="0"
                                step="100"
                                value={allowanceAmount}
                                onChange={(e) => setAllowanceAmount(e.target.value)}
                                placeholder="e.g., 500"
                            />
                        </div>

                        <div className={styles.formField}>
                            <label>Allowance Label</label>
                            <input
                                type="text"
                                value={allowanceLabel}
                                onChange={(e) => setAllowanceLabel(e.target.value)}
                                placeholder="e.g., Festival Bonus, Performance Incentive"
                            />
                        </div>

                        <div className={styles.formField}>
                            <label>Apply To</label>
                            <div className={styles.radioGroup}>
                                <label>
                                    <input
                                        type="radio"
                                        value="ALL"
                                        checked={allowanceScope === 'ALL'}
                                        onChange={() => setAllowanceScope('ALL')}
                                    />
                                    All staff
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        value="SPECIFIC"
                                        checked={allowanceScope === 'SPECIFIC'}
                                        onChange={() => setAllowanceScope('SPECIFIC')}
                                    />
                                    Specific staff (IDs comma-separated)
                                </label>
                            </div>
                            {allowanceScope === 'SPECIFIC' ? (
                                <input
                                    type="text"
                                    value={allowanceStaffIds.join(', ')}
                                    onChange={(e) => {
                                        const ids = e.target.value
                                            .split(',')
                                            .map((id) => id.trim())
                                            .filter(Boolean)
                                        setAllowanceStaffIds(ids)
                                    }}
                                    placeholder="e.g., ID1, ID2, ID3"
                                />
                            ) : null}
                        </div>

                        <div className={styles.formActions}>
                            <button className={styles.secondaryBtn} onClick={() => setSelectedChangeType(null)}>
                                Cancel
                            </button>
                            <button className={styles.primaryBtn} onClick={addAllowanceChange}>
                                Add Change
                            </button>
                        </div>
                    </div>
                )

            case 'REMOVE_COMPONENT':
                return (
                    <div className={styles.changeForm}>
                        <div className={styles.formField}>
                            <label>Component Code</label>
                            <input
                                type="text"
                                value={removeComponentCode}
                                onChange={(e) => setRemoveComponentCode(e.target.value)}
                                placeholder="e.g., LATE_DEDUCTION, ADVANCE_EMI"
                            />
                        </div>

                        <div className={styles.formActions}>
                            <button className={styles.secondaryBtn} onClick={() => setSelectedChangeType(null)}>
                                Cancel
                            </button>
                            <button className={styles.primaryBtn} onClick={addRemoveComponentChange}>
                                Add Change
                            </button>
                        </div>
                    </div>
                )

            default:
                return null
        }
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>

                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <WandSparkles size={12} /> Payroll
                    </span>
                    <h1 className={styles.headerTitle}>Payroll Simulator</h1>
                    <p className={styles.headerSub}>
                        {simResult?.basedOnCycle ? `Based on ${simResult.basedOnCycle}` : 'Test hypothetical payroll changes before applying them.'}
                    </p>
                </div>
            </div>

            {selectedChangeType ? (
                <section className={styles.builderCard}>{renderChangeForm()}</section>
            ) : (
                <>
                    <section className={styles.changeTypePicker}>
                        <div className={styles.pickerLabel}>
                            <CirclePlus size={14} />
                            <span>Select a change type to add</span>
                        </div>
                        <div className={styles.typeGrid}>
                            {CHANGE_TYPES.map((type) => (
                                <button
                                    key={type.key}
                                    className={styles.typeCard}
                                    type="button"
                                    onClick={() => handleChangeTypeSelect(type.key)}
                                >
                                    <span className={styles.typeIcon}>{type.icon}</span>
                                    <span className={styles.typeLabel}>{type.label}</span>
                                </button>
                            ))}
                        </div>
                    </section>

                    {changes.length > 0 ? (
                        <section className={styles.appliedChangesCard}>
                            <div className={styles.appliedHead}>
                                <strong>Applied Changes ({changes.length})</strong>
                                <button className={styles.clearBtn} onClick={clearAllChanges}>
                                    Clear All
                                </button>
                            </div>
                            <div className={styles.changesList}>
                                {changes.map((change, idx) => (
                                    <ChangeChip
                                        key={idx}
                                        change={change}
                                        onRemove={() => removeChange(idx)}
                                    />
                                ))}
                            </div>
                        </section>
                    ) : null}

                    <section className={styles.actionCard}>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={runSimulation}
                            disabled={changes.length === 0 || isSimulating}
                        >
                            {isSimulating ? (
                                <>
                                    <LoaderCircle size={14} className={styles.spinIcon} /> Simulating...
                                </>
                            ) : (
                                <>
                                    <ArrowRight size={14} /> Run Simulation
                                </>
                            )}
                        </button>
                    </section>

                    {simResult ? (
                        <section className={styles.resultsCard}>
                            <button
                                className={styles.resultsToggle}
                                type="button"
                                onClick={() => setResultsExpanded((prev) => !prev)}
                            >
                                <div>
                                    <strong>Simulation Results</strong>
                                    <p>Impact summary and per-staff breakdown</p>
                                </div>
                                {resultsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>

                            {resultsExpanded ? (
                                <div className={styles.resultsBody}>
                                    <div className={styles.impactSummary}>
                                        <div className={styles.impactBox}>
                                            <span>Current Total Net Pay</span>
                                            <strong>₹{formatINR(simResult.current?.totalNetPay || 0)}</strong>
                                        </div>
                                        <div className={styles.impactBox}>
                                            <span>Projected Total Net Pay</span>
                                            <strong>₹{formatINR(simResult.projected?.totalNetPay || 0)}</strong>
                                        </div>
                                        <div className={`${styles.impactBox} ${styles.impactBoxHighlight}`}>
                                            <span>Change</span>
                                            <div className={styles.deltaRow}>
                                                <strong
                                                    style={{
                                                        color: (simResult.projected?.delta || 0) >= 0 ? '#2E7D32' : '#C62828',
                                                    }}
                                                >
                                                    {(simResult.projected?.delta || 0) >= 0 ? '+' : '−'}₹
                                                    {formatINR(Math.abs(simResult.projected?.delta || 0))}
                                                </strong>
                                                {simResult.projected?.deltaPercent !== undefined ? (
                                                    <span
                                                        style={{
                                                            color: (simResult.projected?.delta || 0) >= 0 ? '#2E7D32' : '#C62828',
                                                        }}
                                                    >
                                                        ({(simResult.projected?.delta || 0) >= 0 ? '+' : '−'}
                                                        {formatINR(Math.abs(simResult.projected.deltaPercent), 2)}%)
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className={styles.impactBox}>
                                            <span>Annual Impact</span>
                                            <strong style={{ color: '#1d4ed8' }}>
                                                {(simResult.projected?.annualImpact || 0) >= 0 ? '+' : '−'}₹
                                                {formatINR(Math.abs(simResult.projected?.annualImpact || 0))}
                                            </strong>
                                        </div>
                                    </div>

                                    {simResult.appliedChanges && simResult.appliedChanges.length > 0 ? (
                                        <div className={styles.appliedSummary}>
                                            <strong>Applied Changes Summary</strong>
                                            <ul className={styles.appliedList}>
                                                {simResult.appliedChanges.map((line, idx) => (
                                                    <li key={idx}>• {line}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : null}

                                    {perStaffImpact.length > 0 ? (
                                        <div className={styles.perStaffSection}>
                                            <strong>Per-Staff Impact (sorted by delta)</strong>
                                            <div className={styles.staffTable}>
                                                <div className={styles.staffTableHead}>
                                                    <div className={styles.staffCol}>Staff</div>
                                                    <div className={styles.amountCol}>Current</div>
                                                    <div className={styles.amountCol}>Projected</div>
                                                    <div className={styles.deltaCol}>Change</div>
                                                </div>
                                                <div className={styles.staffTableBody}>
                                                    {perStaffImpact.map((staff) => (
                                                        <div key={staff.staffIdentifier} className={styles.staffRow}>
                                                            <div className={styles.staffCol}>
                                                                <strong>{staff.staffName}</strong>
                                                                <span>{staff.designationLabel || '—'}</span>
                                                            </div>
                                                            <div className={styles.amountCol}>
                                                                ₹{formatINR(staff.currentNetPay)}
                                                            </div>
                                                            <div className={styles.amountCol}>
                                                                ₹{formatINR(staff.projectedNetPay)}
                                                            </div>
                                                            <div
                                                                className={styles.deltaCol}
                                                                style={{
                                                                    color: staff.delta >= 0 ? '#2E7D32' : '#C62828',
                                                                }}
                                                            >
                                                                <div className={styles.deltaValue}>
                                                                    {staff.delta >= 0 ? '+' : '−'}₹{formatINR(Math.abs(staff.delta))}
                                                                </div>
                                                                <div className={styles.deltaPercent}>
                                                                    {staff.delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                                                    {formatINR(Math.abs(staff.deltaPercent), 2)}%
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </section>
                    ) : null}
                </>
            )}
        </div>
    )
}

const ChangeChip = ({ change, onRemove }) => {
    let label = ''

    switch (change?.changeType) {
        case 'COMMISSION_RATE_CHANGE':
            label = `${change.componentType} → ${change.newRate}%${change.templateIdentifier ? ` (${change.templateIdentifier})` : ''}`
            break
        case 'BASIC_INCREMENT':
            if (change.incrementAmount !== undefined) {
                label = `+₹${formatINR(change.incrementAmount)} basic${change.staffIdentifiers ? ` (${change.staffIdentifiers.length} staff)` : ''}`
            } else {
                label = `+${change.incrementPercent}% basic${change.staffIdentifiers ? ` (${change.staffIdentifiers.length} staff)` : ''}`
            }
            break
        case 'ADD_ALLOWANCE':
            label = `+₹${formatINR(change.amountPerStaff)} "${change.allowanceLabel}"${change.appliesToAll ? '' : ` (${change.staffIdentifiers?.length || 0} staff)`}`
            break
        case 'REMOVE_COMPONENT':
            label = `Remove ${change.componentCode}`
            break
        default:
            label = 'Unknown change'
    }

    return (
        <div className={styles.changeChip}>
            <span>{label}</span>
            <button
                type="button"
                className={styles.chipRemoveBtn}
                onClick={onRemove}
                title="Remove this change"
            >
                <X size={14} />
            </button>
        </div>
    )
}

export default PayrollSimulatorScreen